import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BaseEntity } from '@shared/types/base-association';
import { BaseTheme } from '@shared/types/base-theme';
import { tap, map } from 'rxjs/operators';
import {
  BehaviorSubject,
  Observable,
  catchError,
  throwError,
  finalize,
  mergeMap,
} from 'rxjs';
import { environment } from '@environment';
import {
  addNewItemToState,
  updateItemInState,
  deleteItemFromState,
} from '@shared/utils/state.util';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService<ThemeType extends BaseTheme> {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);
  private _baseUrl = environment.backendUrl + '/api/admin/theme';

  private _themes = new BehaviorSubject<ThemeType[]>([]);
  private _isLoading = new BehaviorSubject<boolean>(false);
  private _themesSummary = new BehaviorSubject<BaseEntity[]>([]);

  readonly themes$ = this._themes.asObservable();
  readonly isLoading$ = this._isLoading.asObservable();

  fetchThemes(forced?: boolean): Observable<ThemeType[]> {
    if (this._themes.value.length && !forced) {
      return this.themes$;
    }
    this._isLoading.next(true);
    return this.http.get<{ themes: ThemeType[] }>(this._baseUrl).pipe(
      map((response) => response.themes),
      tap((themes) => {
        this._isLoading.next(false);
        this._themes.next(themes);
      }),
      catchError((err: Error) => {
        this._isLoading.next(false);
        return throwError(() => new Error(err.message));
      }),
    );
  }

  createSingleTheme(theme: ThemeType): Observable<ThemeType> {
    this._isLoading.next(true);
    return this.http.post<ThemeType>(`${this._baseUrl}/single`, theme).pipe(
      tap((response) => addNewItemToState(this._themes, response)),
      tap(() => this._isLoading.next(false)),
      tap(() =>
        this.notificationService.showSuccessNotification('Theme created'),
      ),
      catchError((err: HttpErrorResponse) => {
        this._isLoading.next(false);
        this.notificationService.showErrorNotification(
          'Error adding the theme',
        );
        return throwError(() => new Error(err?.error?.message || err?.message));
      }),
    );
  }

  updateSingleTheme(theme: ThemeType): Observable<ThemeType> {
    this._isLoading.next(true);
    return this.http
      .put<ThemeType>(`${this._baseUrl}/single/${theme.uuid}`, theme)
      .pipe(
        tap((updated) => updateItemInState(this._themes, updated)),
        tap(() => this._isLoading.next(false)),
        tap(() =>
          this.notificationService.showSuccessNotification('Theme updated'),
        ),
        catchError((err: HttpErrorResponse) => {
          this._isLoading.next(false);
          this.notificationService.showErrorNotification(
            'Error updating the theme',
          );
          return throwError(
            () => new Error(err?.error?.message || err?.message),
          );
        }),
      );
  }

  deleteSingleTheme(uuid: string): Observable<void> {
    this._isLoading.next(true);
    return this.http.delete<void>(`${this._baseUrl}/single/${uuid}`).pipe(
      tap(() => {
        this._isLoading.next(false);
        deleteItemFromState(this._themes, uuid);
        this.notificationService.showSuccessNotification(
          'theme.deleteThemeSuccess',
        );
      }),
      catchError((err: Error) => {
        this._isLoading.next(false);
        this.notificationService.showErrorNotification(
          'Error deleting the theme',
        );
        return throwError(() => new Error(err.message));
      }),
    );
  }

  syncMissingVariables(): Observable<ThemeType[]> {
    this._isLoading.next(true);
    return this.http
      .post<void>(`${this._baseUrl}/sync-missing-variables`, {})
      .pipe(
        mergeMap(() => this.fetchThemes(true)),
        tap(() => {
          this.notificationService.showSuccessNotification(
            'boardTheme.syncSuccess',
          );
        }),
        catchError((error) => {
          this.notificationService.showErrorNotification(
            'boardTheme.syncError',
          );
          return throwError(() => error);
        }),
        finalize(() => this._isLoading.next(false)),
      );
  }

  resetData(): void {
    this._themes.next([]);
    this._themesSummary.next([]);
  }
}
